import { useId } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { usePartners } from '@/hooks/usePartners'
import { site } from '@/config/site'
import { alliances } from '@/config/alliances'
import { Picture } from '@/components/ui/Picture'
import { ButtonLink } from '@/components/ui/Button'

export function Partners() {
  const headingId = useId()
  const { partners, featuredPartner, loading, error } = usePartners()
  const others = partners.filter((p) => p.id !== featuredPartner?.id)

  return (
    <section
      id="partners"
      aria-labelledby={headingId}
      className="bg-surface py-section-y"
    >
      <div className="mx-auto max-w-[88rem] px-6 sm:px-10">
        <header className="grid grid-cols-1 gap-y-6 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <h2
              id={headingId}
              className="font-display text-display-2 font-extrabold text-ink-deep"
            >
              Founding LifeSpan Partners
            </h2>
          </div>
          <p className="text-base leading-relaxed text-ink-slate lg:col-span-5 lg:col-start-8 lg:pt-3 lg:text-lg">
            Organizations are rebuilding the systems for a longer life with GIA
            Longevity. Founding LifeSpan Partners are industry innovators with a
            vision for future opportunity.
          </p>
        </header>

        {error && (
          <p
            role="alert"
            className="mt-10 border border-hairline bg-surface-alt p-4 text-sm text-ink-slate"
          >
            Could not load partners. Please try again later.
          </p>
        )}

        {/* Featured founding partner — full-bleed dark band */}
        {featuredPartner && (
          <article className="mt-14 grid grid-cols-1 gap-y-8 bg-ink-deep p-8 text-surface sm:p-12 lg:grid-cols-12 lg:gap-x-12 lg:p-16">
            <div className="lg:col-span-3">
              <span className="eyebrow text-gia-blue-light-2">
                LifeSpan Founding Partner
              </span>
              <div className="mt-5 border-t border-surface/15 pt-5">
                {featuredPartner.logo_url ? (
                  <img
                    src={featuredPartner.logo_url}
                    alt={`${featuredPartner.name} logo`}
                    className="max-h-12 w-auto brightness-0 invert"
                  />
                ) : (
                  <p className="font-display text-3xl font-extrabold tracking-tight">
                    {featuredPartner.name}
                  </p>
                )}
              </div>
            </div>
            <div className="lg:col-span-8 lg:col-start-5">
              <p className="font-display text-2xl font-extrabold leading-snug text-surface sm:text-3xl">
                {featuredPartner.name} is the first U.S. company to join the
                ecosystem as a founding partner, co-designing across work,
                care, and the longer customer life.
              </p>
              <a
                href={site.uberAnnouncementUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-[0.08em] text-surface hover:text-gia-blue-light-2"
              >
                Read about the partnership
                <ArrowUpRight className="h-4 w-4" aria-hidden />
              </a>
            </div>
          </article>
        )}

        {/* Founding cohort strip */}
        <PartnerStrip
          title="LifeSpan Founding Partners"
          count={others.length}
          loading={loading}
          emptyCopy={
            <>
              Ten founding partnerships are being assembled. Additional
              founding partners will appear here as they are confirmed. If
              your organization is interested,{' '}
              <a
                href="#contact"
                className="font-bold text-ink-deep underline decoration-1 underline-offset-4 hover:no-underline"
              >
                get in touch
              </a>
              .
            </>
          }
        >
          {others}
        </PartnerStrip>

        {/* Categories planned per Website Review — populated as they sign on */}
        <PartnerStrip
          title="LifeSpan Members"
          loading={false}
          emptyCopy="An open membership tier for organizations joining the longevity ecosystem. Members will be listed here."
        >
          {[]}
        </PartnerStrip>

        <div className="mt-16">
          <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-b border-hairline pb-3">
            <span className="eyebrow">Strategic Alliances</span>
            <p className="text-xs font-bold uppercase tracking-[0.12em] text-ink-faint">
              {alliances.length} confirmed
            </p>
          </div>
          <ul role="list" className="mt-8 space-y-6">
            {alliances.map((a) => (
              <li
                key={a.id}
                className="grid grid-cols-1 items-center gap-6 sm:grid-cols-[9rem_1fr] sm:gap-10"
              >
                <a
                  href={a.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${a.name} website (opens in new tab)`}
                  className="block w-28 sm:w-36"
                >
                  <Picture
                    name={a.logo}
                    sizes="(min-width: 640px) 144px, 112px"
                    alt={`${a.name} logo`}
                    className="h-auto w-full"
                  />
                </a>
                <p className="max-w-2xl font-display text-lg font-bold leading-snug text-ink-deep sm:text-xl">
                  {a.description}
                </p>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-14">
          <ButtonLink href="#contact" variant="primary" size="lg">
            Become a partner
          </ButtonLink>
        </div>
      </div>
    </section>
  )
}

interface StripProps {
  title: string
  count?: number
  loading: boolean
  emptyCopy: React.ReactNode
  children: ReturnType<typeof usePartners>['partners']
}

function PartnerStrip({ title, count, loading, emptyCopy, children }: StripProps) {
  const items = children
  return (
    <div className="mt-16">
      <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-b border-hairline pb-3">
        <span className="eyebrow">{title}</span>
        <p className="text-xs font-bold uppercase tracking-[0.12em] text-ink-faint">
          {count && count > 0 ? `${count} confirmed` : 'In assembly'}
        </p>
      </div>

      {loading ? (
        <ul className="mt-8 grid grid-cols-2 gap-px bg-hairline sm:grid-cols-3 lg:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <li key={i} className="h-24 animate-pulse bg-surface-alt" aria-hidden />
          ))}
        </ul>
      ) : items.length === 0 ? (
        <p className="mt-6 max-w-md text-sm leading-relaxed text-ink-slate">
          {emptyCopy}
        </p>
      ) : (
        <ul
          role="list"
          className="mt-8 grid grid-cols-2 gap-px bg-hairline sm:grid-cols-3 lg:grid-cols-6"
        >
          {items.map((p) => {
            const inner = p.logo_url ? (
              <img
                src={p.logo_url}
                alt={`${p.name} logo`}
                className="max-h-10 w-auto grayscale opacity-70 transition-opacity duration-300 ease-out-quart hover:opacity-100 hover:grayscale-0"
              />
            ) : (
              <span className="font-display text-sm font-extrabold text-ink-deep">
                {p.name}
              </span>
            )
            return (
              <li
                key={p.id}
                className="flex h-24 items-center justify-center bg-surface"
              >
                {p.website_url ? (
                  <a
                    href={p.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${p.name} website`}
                    className="flex h-full w-full items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
                  >
                    {inner}
                  </a>
                ) : (
                  inner
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
